'use client'

import * as React from 'react'
import { Check, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PricingSlider } from './pricing-slider'
import { useTranslations } from 'next-intl'

interface Tier {
  id: string
  name: string
  price: number | 'Custom'
  memberLimit: number | null
  bestFor: string
  unitPrice: string | null
  savings: string | null
  cta: string
  highlighted: boolean
  features?: string[] // Optional features list
}

interface PricingCalculatorProps {
  tiers: Tier[]
}

export function PricingCalculator({ tiers }: PricingCalculatorProps) {
  // Default to Free tier (first one) or "growth" if preferred, but plan says "Default the view to the Free tier"
  // finding index of 'free' or 0
  const freeTierIndex = tiers.findIndex((t) => t.id === 'free')
  const defaultTierId = freeTierIndex !== -1 ? tiers[freeTierIndex].id : tiers[0].id

  const [selectedTierId, setSelectedTierId] = React.useState<string>(defaultTierId)
  const t = useTranslations('landing')

  const selectedTier = React.useMemo(
    () => tiers.find((t) => t.id === selectedTierId) || tiers[0],
    [tiers, selectedTierId]
  )

  // Determine if this is the highest tier to show "Contact Sales" logic if needed,
  // but we have a separate Enterprise card usually.
  // For now, we assume selectedTier is one of the standard paid tiers.

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Growth Engine (Slider & Value Props) */}
        <div className="lg:col-span-7 flex flex-col justify-center py-8 space-y-8">
          <div>
            <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
              {t('pricing.calculator.title')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('pricing.calculator.subtitle')}</p>
          </div>

          <div className="bg-muted/30 p-8 rounded-2xl border border-border/50">
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t('pricing.calculator.howManyMembers')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t('pricing.calculator.verifyHint')}
            </p>

            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-4xl font-bold text-primary">
                {selectedTier.memberLimit === null
                  ? '7,500+'
                  : selectedTier.memberLimit.toLocaleString()}
              </span>
              <span className="text-lg text-muted-foreground">
                {t('pricing.calculator.members')}
              </span>
            </div>

            <PricingSlider
              tiers={tiers}
              selectedTierId={selectedTierId}
              onSelectTier={setSelectedTierId}
              className="px-0 py-4 w-full"
            />

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-background border shadow-sm">
                <div className="text-sm text-muted-foreground mb-1">
                  {t('pricing.calculator.plan')}
                </div>
                <div className="font-semibold text-lg">{selectedTier.name}</div>
              </div>
              <div className="p-4 rounded-xl bg-background border shadow-sm">
                <div className="text-sm text-muted-foreground mb-1">
                  {t('pricing.calculator.pricePerMember')}
                </div>
                <div className="font-semibold text-lg">
                  {selectedTier.unitPrice === 'Custom' ? (
                    t('pricing.calculator.contactUs')
                  ) : selectedTier.unitPrice ? (
                    <span>{selectedTier.unitPrice}</span>
                  ) : (
                    t('pricing.calculator.free')
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Value Props / Features Summary */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Check className="h-3.5 w-3.5 text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {t('pricing.calculator.transparentPricing')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('pricing.calculator.transparentPricingDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Check className="h-3.5 w-3.5 text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {t('pricing.calculator.fullAccess')}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t('pricing.calculator.fullAccessDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Dynamic Card (Plan Preview) */}
        <div className="lg:col-span-5">
          <Card className="relative overflow-hidden border-2 border-border shadow-lg transition-all duration-300">
            <CardContent className="p-8 flex flex-col h-full min-h-[500px]">
              <div className="mb-6">
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  {selectedTier.bestFor}
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-2">{selectedTier.name}</h3>
                <div className="flex items-baseline gap-1 mt-4">
                  <span className="text-5xl font-bold text-foreground">
                    {selectedTier.price === 'Custom'
                      ? t('pricing.calculator.custom')
                      : selectedTier.price === 0
                        ? t('pricing.calculator.free')
                        : `$${selectedTier.price}`}
                  </span>
                  {selectedTier.price !== 0 && selectedTier.price !== 'Custom' && (
                    <span className="text-xl text-muted-foreground">/mo</span>
                  )}
                </div>
                {selectedTier.savings && (
                  <div className="mt-2 text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 py-1 px-3 rounded-full inline-block">
                    {t('pricing.calculator.savingsVsStarter', { savings: selectedTier.savings })}
                  </div>
                )}
              </div>

              <div className="border-t border-border my-6"></div>

              <div className="grow space-y-4 mb-8">
                <p className="font-medium text-foreground">
                  {selectedTier.id === 'free'
                    ? t('pricing.calculator.allCoreFeatures')
                    : t('pricing.calculator.everythingPlusPrevious')}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    <span>
                      {t('pricing.calculator.upToMembers', {
                        count:
                          selectedTier.memberLimit === null
                            ? t('pricing.calculator.unlimited')
                            : selectedTier.memberLimit.toLocaleString(),
                      })}
                    </span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    <span>{t('pricing.calculator.fullEngagementSuite')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0" />
                    <span>{t('pricing.calculator.adminDashboard')}</span>
                  </li>
                  {selectedTier.price !== 0 && selectedTier.price !== 'Custom' && (
                    <li className="flex items-center gap-3 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span>{t('pricing.calculator.prioritySupport')}</span>
                    </li>
                  )}
                  {(selectedTier.price === 0 || selectedTier.price === 'Custom') && (
                    <li className="flex items-center gap-3 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span>
                        {selectedTier.price === 'Custom'
                          ? t('pricing.calculator.dedicatedSupport')
                          : t('pricing.calculator.communitySupport')}
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              <Button
                size="lg"
                className="w-full text-lg h-12"
                variant={selectedTier.highlighted ? 'default' : 'outline'}
              >
                {selectedTier.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="mt-4 text-center">
                <p className="text-xs text-muted-foreground">
                  {selectedTier.price === 0
                    ? t('pricing.calculator.noCreditCard')
                    : selectedTier.price === 'Custom'
                      ? t('pricing.calculator.talkToEnterprise')
                      : t('pricing.calculator.startFreeUpgrade')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
